/**
 * Variáveis canônicas de mensagem — ESPELHO de `packages/db/src/templating.ts`.
 * O frontend não consome packages do workspace, então a lógica é duplicada aqui.
 * ⚠️ Manter os DOIS em sincronia (mesma lista de tokens + semântica).
 *
 * Tokens: {{firstname}}, {{name}} (= {{full_name}}), {{phone}}, {{email}},
 * {{state_uf}}, {{custom.CAMPO}}, apelidos {{contact.X}}, e atendente
 * {{atendente}}/{{agente}}/{{user.name}}. Token canônico sem valor → vazio;
 * token não-canônico (typo) → fica literal.
 */

export type ContactVars = {
  displayName?: string | null | undefined;
  phone?: string | null | undefined;
  email?: string | null | undefined;
  stateUf?: string | null | undefined;
  customFields?: Record<string, unknown> | null | undefined;
};

export type CanonicalVarContext = {
  contact?: ContactVars | null | undefined;
  userName?: string | null | undefined;
};

function firstName(full?: string | null): string {
  return (full ?? '').trim().split(/\s+/)[0] ?? '';
}

export function resolveCanonicalToken(rawToken: string, ctx: CanonicalVarContext): string | null {
  const token = rawToken.trim();
  const c = ctx.contact;
  switch (token.toLowerCase()) {
    case 'firstname':
      return firstName(c?.displayName);
    case 'name':
    case 'full_name':
    case 'contact.display_name':
      return c?.displayName ?? '';
    case 'phone':
    case 'contact.phone':
      return c?.phone ?? '';
    case 'email':
    case 'contact.email':
      return c?.email ?? '';
    case 'state_uf':
    case 'contact.state_uf':
      return c?.stateUf ?? '';
    case 'atendente':
    case 'agente':
    case 'user.name':
      return ctx.userName ?? '';
  }
  const m = token.match(/^(?:custom|contact\.custom_field)\.(.+)$/i);
  if (m) {
    const fields = (c?.customFields ?? {}) as Record<string, unknown>;
    const v = fields[m[1] as string];
    return v === undefined || v === null ? '' : String(v);
  }
  return null;
}

// Inclui hífen pra cobrir chaves de custom field com "-" (ex: {{custom.estado-uf}}).
const TOKEN_RE = /\{\{\s*([\w.-]+)\s*\}\}/g;

export function renderContactVars(text: string, ctx: CanonicalVarContext): string {
  if (!text) return text;
  return text.replace(TOKEN_RE, (full: string, token: string) => {
    const resolved = resolveCanonicalToken(token, ctx);
    return resolved === null ? full : resolved;
  });
}

/** Tokens canônicos pra dicas de UI. */
export const CANONICAL_VAR_HINTS = [
  '{{firstname}}',
  '{{name}}',
  '{{phone}}',
  '{{email}}',
  '{{state_uf}}',
  '{{custom.CAMPO}}',
] as const;
